<template>
  <div class="flex w-full flex-col items-center bg-gray-50 pb-6 text-gray-700 md:pb-10">
    <div class="mb-10 flex w-full max-w-4xl flex-col rounded-md bg-white px-5 py-6 text-gray-700 md:p-10">
      <img
        :src="maker.logoImage?.derivatives?.jpgMd?.url"
        :alt="`Logo de ${maker.name}`"
        class="size-20 rounded-full object-cover"
      />
      <div class="mb-6 flex items-center justify-between border-b border-gray-200 pb-4">
        <div class="flex flex-col">
          <h1 class="text-2xl font-bold text-gray-700 md:text-3xl">
            {{ products[0].makerName }} Cotización #{{ preOrder.id }}
          </h1>
        </div>
        <div class="flex flex-col items-end">
          <span
            class="mb-1 rounded-full px-3 py-1 text-xs font-medium uppercase text-white md:px-4 md:py-2 md:text-sm"
            :class="getStatusClasses(preOrder.status)"
          >
            {{ t(`preOrder.status.${camelCase(preOrder.status)}`) }}
          </span>
          <p
            v-if="preOrder.expiresAt && preOrder.status !== 'expired'"
            class="text-right text-xs text-gray-500 md:text-sm"
          >
            Válida hasta: {{ formatDate(preOrder.expiresAt) }}
          </p>
        </div>
      </div>

      <div class="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2">
        <div class="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <h3 class="mb-3 text-lg font-semibold">Información de Contacto</h3>
          <div class="space-y-2 text-sm md:text-base">
            <div class="flex flex-col">
              <span class="font-medium">Nombre:</span>
              <span class="font-light">{{ preOrder.contactName }}</span>
            </div>
            <div class="flex flex-col">
              <span class="font-medium">Email:</span>
              <span class="font-light">{{ preOrder.contactEmail }}</span>
            </div>
            <div
              v-if="preOrder.contactNumber"
              class="flex flex-col"
            >
              <span class="font-medium">Teléfono:</span>
              <span class="font-light">{{ preOrder.contactNumber }}</span>
            </div>
          </div>
        </div>

        <div class="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <h3 class="mb-3 text-lg font-semibold">Información Empresarial</h3>
          <div class="space-y-2 text-sm md:text-base">
            <div class="flex flex-col">
              <span class="font-medium">RUT:</span>
              <span class="font-light">{{ preOrder.businessRut }}</span>
            </div>
            <div class="flex flex-col">
              <span class="font-medium">Razón Social:</span>
              <span class="font-light">{{ preOrder.socialReason }}</span>
            </div>
            <div class="flex flex-col">
              <span class="font-medium">Tipo:</span>
              <span class="font-light">
                {{ preOrder.commercialBusiness ? 'Empresa Comercial' : 'Empresa No Comercial' }}
              </span>
            </div>
            <div class="flex flex-col">
              <span class="font-medium">Dirección:</span>
              <span class="font-light"
                >{{ preOrder.address }}, {{ preOrder.communeName }}, {{ preOrder.regionName }}</span
              >
            </div>
            <div class="flex flex-col">
              <span class="font-medium">Tipo de Tienda:</span>
              <span class="font-light">{{ t(`shop.kind.${camelCase(preOrder.shopKind)}`) }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="mb-8">
        <h3 class="mb-4 text-lg font-semibold">Productos Cotizados</h3>
        <div class="overflow-x-auto rounded-lg border border-gray-200">
          <table class="w-full text-xs md:text-sm">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-2 py-3 text-left font-medium md:px-4">Producto</th>
                <th class="px-2 py-3 text-left font-medium md:px-4">Nombre</th>
                <th class="px-2 py-3 text-center font-medium md:px-4">Cantidad</th>
                <th class="hidden px-2 py-3 text-center font-medium md:block md:px-4">Precio Unitario</th>
                <th class="px-2 py-3 text-center font-medium md:px-4">Precio Mayorista</th>
                <th class="px-2 py-3 text-center font-medium md:px-4">Subtotal</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr
                v-for="preOrderProduct in preOrderProducts"
                :key="preOrderProduct.id"
                class="hover:bg-gray-50"
              >
                <pre-order-show-product-card
                  :pre-order-product="preOrderProduct"
                  :product="getProductById(preOrderProduct.productId)"
                  @update:totalProductsPriceCents="updateTotalProductsPriceCents"
                />
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="mb-8 flex justify-end">
        <div class="w-full max-w-md space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div class="flex justify-between text-sm">
            <span class="font-medium">Total Productos:</span>
            <span class="">{{ totalProductsCount }}</span>
          </div>
          <div class="flex justify-between text-sm">
            <span class="font-medium">Total Unidades:</span>
            <span class="">{{ totalUnitsCount }}</span>
          </div>
          <div class="border-t border-gray-300 pt-2">
            <div class="flex justify-between text-base font-semibold">
              <span class="">Total Mayorista Neto:</span>
              <span class="">{{ useFormatCurrency(totalWholesaleAmount) }}</span>
            </div>
            <div class="flex justify-between text-base font-semibold">
              <span class="">Total Mayorista Bruto:</span>
              <span class="">{{ useFormatCurrency(round(totalWholesaleAmount * 1.19)) }}</span>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="preOrder.notes"
        class="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4"
      >
        <h3 class="mb-2 text-lg font-semibold">Notas Adicionales</h3>
        <p class="text-sm md:text-base">
          {{ preOrder.notes }}
        </p>
      </div>

      <div class="border-t border-gray-200 pt-4 text-center text-sm text-gray-500">
        <p>Pre-orden generada el {{ formatDate(preOrder.createdAt) }}</p>
        <p v-if="preOrder.expiresAt">Válida hasta el {{ formatDate(preOrder.expiresAt) }}</p>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { camelCase, round } from 'lodash';
import preOrderShowProductCard from './pre-order-show-product-card.vue';
import useFormatCurrency from './use/format-currency';

interface PreOrderShowProps {
  maker: Maker;
  preOrder: PreOrder;
  preOrderProducts: PreOrderProduct[];
  products: Product[];
}

const props = defineProps<PreOrderShowProps>();

const { t } = useI18n();

const totalProductsPriceCents = ref({} as Record<number, number>);

function updateTotalProductsPriceCents(value: Record<number, number>) {
  totalProductsPriceCents.value = { ...totalProductsPriceCents.value, ...value };
}

function getProductById(productId: number): Product {
  const product = props.products.find((product) => product.id === productId);
  if (!product) {
    throw new Error(`Product with id ${productId} not found`);
  }
  return product;
}

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('es-CL');
};

function getStatusClasses(status: string): string {
  if (status === 'draft') {
    return 'bg-gray-500';
  } else if (status === 'sent') {
    return 'bg-blue-500';
  } else if (status === 'accepted') {
    return 'bg-green-500';
  } else if (status === 'rejected') {
    return 'bg-red-500';
  } else if (status === 'expired') {
    return 'bg-orange-500';
  } else if (status === 'converted') {
    return 'bg-purple-500';
  }
  return 'bg-gray-500';
}

const totalProductsCount = computed(() => props.preOrderProducts.length);

const totalUnitsCount = computed(() => props.preOrderProducts.reduce((sum, item) => sum + item.quantity, 0));

const totalWholesaleAmount = computed(() =>
  Object.values(totalProductsPriceCents.value).reduce((sum, price) => sum + price, 0),
);
</script>
